import * as React from 'react';
import { Column, defaultColumnSizing } from '@tanstack/react-table';
import { cx } from './cx';
import { GRID_COLUMN_ATTRIBUTE, GRID_OWNER_ATTRIBUTE, GRID_ROW_ATTRIBUTE } from './use-grid-navigation';
import styles from './data-table.module.css';

/** TanStack merges these defaults into every `columnDef`; a `minSize` equal to the default reads as unset. */
const { minSize: DEFAULT_MIN_SIZE, maxSize: UNBOUNDED_MAX_SIZE } = defaultColumnSizing;

export interface DataTableCellProps<TData> extends React.ThHTMLAttributes<HTMLTableCellElement> {
    /** Real table elements only. Never a div: four stylesheets and five existing tests depend on them. */
    as: 'th' | 'td';
    column: Column<TData, unknown>;
    /** `header.getSize()` for header cells (it accounts for group colSpan), `column.getSize()` for body cells. */
    size: number;
    /** Grid coordinates, set only while `keyboardNavigation` is on. See `use-grid-navigation.ts`. */
    gridRow?: number;
    gridColumn?: number;
    /** Owning table instance. Travels with the coordinates so a nested grid cannot claim this cell. */
    gridOwner?: string;
    children?: React.ReactNode;
}

/**
 * The single `th`/`td` primitive, owning all the cell geometry: flex sizing, pinning offsets, alignment, and
 * a guaranteed `position: relative` so callers can absolutely position affordances inside a cell.
 *
 * `meta.grow` is ignored while a column is pinned: the sticky offsets are sums of the pinned columns'
 * `getSize()`, which a growing pinned column would invalidate.
 */
export const DataTableCell = <TData,>(props: DataTableCellProps<TData>): React.ReactElement => {
    const { as, column, size, gridRow, gridColumn, gridOwner, className, style, children, scope, ...rest } = props;
    const isGridCell = gridRow !== undefined && gridColumn !== undefined && gridOwner !== undefined;

    const meta = column.columnDef.meta;
    const pinned = column.getIsPinned();
    // a `meta.rowHeader` body cell is a `th` too, but it is styled and classed as a body cell
    const isHeader = as === 'th' && scope !== 'row';
    const grow = meta?.grow === true && pinned === false;
    const align = meta?.align ?? 'left';
    const minSize = column.columnDef.minSize;
    const maxSize = column.columnDef.maxSize;

    // `.data-table` is `max-content` wide, the sum of these bases (the inline `width` below is what makes
    // that so). A growing column starts at its explicit `minSize` so the table scrolls later, and spare width
    // is shared in proportion to `size`, whatever the floor.
    const hasExplicitMinSize = minSize !== undefined && minSize !== DEFAULT_MIN_SIZE;
    const basis = grow && hasExplicitMinSize ? minSize : size;

    const resolvedStyle: React.CSSProperties = {
        flexGrow: grow ? size : 0,
        flexShrink: grow ? 1 : 0,
        flexBasis: `${basis}px`,
        width: `${basis}px`,
        // a non-growing column can never shrink, so its floor is its own size
        minWidth: `${Math.max(minSize ?? 0, grow ? 0 : size)}px`,
        maxWidth: maxSize !== undefined && maxSize < UNBOUNDED_MAX_SIZE ? `${maxSize}px` : undefined,
        ...(pinned === 'left' ? { position: 'sticky' as const, left: `${column.getStart('left')}px` } : {}),
        ...(pinned === 'right' ? { position: 'sticky' as const, right: `${column.getAfter('right')}px` } : {}),
        ...style,
    };

    const resolvedClassName = cx(
        styles['data-table-cell'],
        isHeader ? styles['data-table-header-cell'] : styles['data-table-body-cell'],
        align !== 'left' && styles[`data-table-cell-align-${align}`],
        // body cells only: stretching a filter trigger to full header width would swallow the cell's
        // remaining click target for sorting
        !isHeader && meta?.fill === true && styles['data-table-cell-fill'],
        pinned !== false && styles['data-table-cell-pinned'],
        pinned !== false && styles[`data-table-cell-pinned-${pinned}`],
        isHeader ? meta?.headerClassName : meta?.className,
        className,
    );

    const shared = {
        className: resolvedClassName,
        style: resolvedStyle,
        'data-column-id': column.id,
        scope,
        ...rest,
        // always -1: `use-grid-navigation.ts` promotes exactly one cell imperatively, and a value React
        // re-rendered here would fight it
        ...(isGridCell
            ? {
                  [GRID_OWNER_ATTRIBUTE]: gridOwner,
                  [GRID_ROW_ATTRIBUTE]: gridRow,
                  [GRID_COLUMN_ATTRIBUTE]: gridColumn,
                  tabIndex: -1,
              }
            : {}),
    };

    return as === 'th' ? <th {...shared}>{children}</th> : <td {...shared}>{children}</td>;
};

/** Element, role and scope for a body or summary cell: `th scope="row"` when the column is a `meta.rowHeader`. */
export const bodyCellElement = <TData,>(
    column: Column<TData, unknown>,
    isGrid: boolean,
): Pick<DataTableCellProps<TData>, 'as' | 'role' | 'scope'> =>
    column.columnDef.meta?.rowHeader === true
        ? { as: 'th', role: 'rowheader', scope: 'row' }
        : { as: 'td', role: isGrid ? 'gridcell' : 'cell' };
